import { useState, useEffect, useRef, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { FiDownload, FiFile, FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { isPdfFile, isImageFile, canViewInline as canViewInlineUtil } from "../../Common/Utils/fileTypeUtils";
import {
  DocumentViewerOverlay,
  DocumentViewerContainer,
  DocumentViewerHeader,
  DocumentViewerTitle,
  DocumentViewerCloseButton,
  DocumentViewerContent,
  DocumentViewerActions,
  DocumentViewerDownloadButton,
  DocumentViewerUnsupported,
  DocumentViewerUnsupportedIcon,
  DocumentViewerUnsupportedText,
  DocumentViewerUnsupportedButton,
  PDFViewerWrapper,
  PDFControls,
  PDFPageInfo,
  PDFNavButton,
  ImageViewer,
} from "./Styles";

// Configure pdf.js worker for react-pdf v10 / pdfjs-dist v5
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

interface DocumentViewerProps {
  documentUrl: string | null;
  fileName: string | null;
  mimeType?: string;
  onClose?: () => void;
}

export const DocumentViewer = ({
  documentUrl,
  fileName,
  mimeType,
  onClose,
}: DocumentViewerProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState<number | null>(null);
  const [pdfFile, setPdfFile] = useState<string | ArrayBuffer | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const pdfBlobUrlRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);


  useEffect(() => {
    if (documentUrl) {
      setError(null);
      setPageNumber(1);
      setNumPages(null);

      // Check if it's a PDF and fetch as blob to handle download headers
      if (isPdfFile(fileName, mimeType) && documentUrl) {
        // Set loading state to prevent Document from rendering with documentUrl
        // while we're fetching the blob
        setPdfFile(null);
        setPdfLoading(true);
        // Abort any previous fetch request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Create new AbortController to cancel fetch if component unmounts or documentUrl changes
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const signal = abortController.signal;

        // Fetch PDF as blob to avoid download headers
        // Use more permissive fetch options to handle CORS and authentication
        fetch(documentUrl, {
          signal,
          mode: 'cors',
          credentials: 'omit',
          cache: 'default'
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
            }
            return response.blob();
          })
          .then((blob) => {
            // Only update state if the request wasn't aborted
            if (!signal.aborted) {
              // Cleanup previous blob URL if exists
              if (pdfBlobUrlRef.current) {
                URL.revokeObjectURL(pdfBlobUrlRef.current);
              }
              const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
              const blobUrl = URL.createObjectURL(pdfBlob);
              pdfBlobUrlRef.current = blobUrl;
              setPdfFile(blobUrl);
              setPdfLoading(false);
            }
          })
          .catch((err) => {
            // Ignore abort errors as they're expected when switching documents
            if (err.name === 'AbortError') {
              return;
            }
            // Only update state if the request wasn't aborted
            if (!signal.aborted) {
              console.warn("Error fetching PDF as blob, will try direct URL:", err);
              // Fall back to direct URL if blob fetch fails
              setPdfFile(documentUrl);
              setPdfLoading(false);
            }
          });
      } else {
        setPdfFile(null);
        setPdfLoading(false);
      }
    }

    // Cleanup blob URL and abort any ongoing fetch on unmount or when documentUrl changes
    return () => {
      // Abort any ongoing fetch request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Cleanup blob URL
      if (pdfBlobUrlRef.current) {
        URL.revokeObjectURL(pdfBlobUrlRef.current);
        pdfBlobUrlRef.current = null;
      }
    };
  }, [documentUrl, fileName, mimeType]);

  useEffect(() => {
    // Calculate page width based on container
    const updateWidth = () => {
      const container = document.querySelector('[data-pdf-container]');
      if (container) {
        const isMobile = window.innerWidth <= 768;
        const padding = isMobile ? 16 : 80; // Less padding on mobile
        const width = (container as HTMLElement).offsetWidth - padding;
        const maxWidth = isMobile ? window.innerWidth - padding : 900;
        setPageWidth(Math.min(width, maxWidth));
      }
    };

    if (documentUrl) {
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
  }, [documentUrl]);

  useEffect(() => {
    // Close modal on ESC key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose) {
        onClose();
      }
    };

    if (documentUrl && onClose) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [documentUrl, onClose]);

  const handleDownload = () => {
    if (!documentUrl || !fileName) return;
    const link = document.createElement("a");
    link.href = documentUrl;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!documentUrl || !onClose) {
    return null;
  }

  const isPDF = isPdfFile(fileName, mimeType);
  const isImage = isImageFile(fileName, mimeType);
  const canViewInline = canViewInlineUtil(fileName, mimeType);

  const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  return (
    <DocumentViewerOverlay onClick={handleOverlayClick}>
      <DocumentViewerContainer onClick={(e) => e.stopPropagation()}>
        <DocumentViewerHeader>
          <DocumentViewerTitle title={fileName || undefined}>{fileName || t("common.documentViewer.fallbackTitle")}</DocumentViewerTitle>
          <DocumentViewerCloseButton onClick={onClose} aria-label={t("common.documentViewer.closeAriaLabel")}>
            <FiX />
          </DocumentViewerCloseButton>
        </DocumentViewerHeader>

      <DocumentViewerContent>
        {error && (
          <div style={{ textAlign: "center", padding: "2rem", color: "#f44336" }}>
            {t("common.documentViewer.errorLoadingDocument", { error })}
            <br />
            <DocumentViewerUnsupportedButton
              onClick={handleDownload}
              style={{ marginTop: "1rem" }}
            >
              <FiDownload />
              {t("common.documentViewer.downloadDocumentButton")}
            </DocumentViewerUnsupportedButton>
          </div>
        )}

        {!canViewInline && !error && (
          <DocumentViewerUnsupported>
            <DocumentViewerUnsupportedIcon>
              <FiFile size={48} />
            </DocumentViewerUnsupportedIcon>
            <DocumentViewerUnsupportedText>
              {t("common.documentViewer.unsupportedText")}
            </DocumentViewerUnsupportedText>
            <DocumentViewerUnsupportedButton onClick={handleDownload}>
              <FiDownload />
              {t("common.documentViewer.downloadDocumentButton")}
            </DocumentViewerUnsupportedButton>
          </DocumentViewerUnsupported>
        )}

        {canViewInline && !error && (
          <>
            {isPDF && (
              <>
                <PDFViewerWrapper data-pdf-container>
                  {pdfLoading ? (
                    <div style={{ textAlign: "center", padding: "2rem" }}>
                      {t("common.documentViewer.loadingPdf")}
                    </div>
                  ) : pdfFile ? (
                    <Document
                      file={pdfFile}
                      loading={
                        <div style={{ textAlign: "center", padding: "2rem" }}>
                          {t("common.documentViewer.processingPdf")}
                        </div>
                      }
                      onLoadSuccess={({ numPages }: { numPages: number }) => {
                        setNumPages(numPages);
                        setError(null);
                      }}
                      onLoadError={(error: Error) => {
                        console.error("Error loading PDF:", error);
                        setError(t("common.documentViewer.loadPdfError"));
                      }}
                      error={
                        <div style={{ textAlign: "center", padding: "2rem", color: "#f44336" }}>
                          {t("common.documentViewer.pdfErrorGeneric")}
                        </div>
                      }
                    >
                      {pageWidth && (
                        <Page
                          pageNumber={pageNumber}
                          width={pageWidth}
                          renderTextLayer={true}
                          renderAnnotationLayer={true}
                          loading={
                            <div style={{ textAlign: "center", padding: "1rem" }}>
                              {t("common.documentViewer.loadingPage")}
                            </div>
                          }
                        />
                      )}
                    </Document>
                  ) : null}
                </PDFViewerWrapper>
                {numPages && numPages > 1 && (
                  <PDFControls>
                    <PDFNavButton
                      onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                      disabled={pageNumber <= 1}
                    >
                      <FiChevronLeft />
                      {t("common.documentViewer.prevPage")}
                    </PDFNavButton>
                    <PDFPageInfo>
                      {t("common.documentViewer.pageInfo", { page: pageNumber, total: numPages })}
                    </PDFPageInfo>
                    <PDFNavButton
                      onClick={() => setPageNumber((prev) => Math.min(numPages, prev + 1))}
                      disabled={pageNumber >= numPages}
                    >
                      {t("common.documentViewer.nextPage")}
                      <FiChevronRight />
                    </PDFNavButton>
                  </PDFControls>
                )}
              </>
            )}

            {isImage && (
              <ImageViewer
                src={documentUrl}
                alt={fileName || ""}
                onError={() => {
                  setError(t("common.documentViewer.loadImageError"));
                }}
              />
            )}
          </>
        )}
      </DocumentViewerContent>

      <DocumentViewerActions>
        <DocumentViewerDownloadButton onClick={handleDownload}>
          <FiDownload />
          {t("common.documentViewer.downloadButton")}
        </DocumentViewerDownloadButton>
      </DocumentViewerActions>
      </DocumentViewerContainer>
    </DocumentViewerOverlay>
  );
};




